'use client';

/**
 * VIDEO LAB ONE-FOCUS DECK — labs are the cards (/api/funnels/videolab/
 * transcribe → labs w/ segments + proposal/output state). Focus: WHY = lab
 * state + pipeline position, PREVIEW = retention curve for the funnel's main
 * VSL (/api/funnels/[id]/retention — real cliffs), DECIDE = APPLY (GET
 * /videolab/apply?job=) when a proposal exists, deep-link to the classic lab.
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, bigVerb, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

function Why({ item }: { item: OFItem }) {
  const l = item.raw;
  const stage = l.hasOutput ? 'OUTPUT RENDERED — READY TO DEPLOY' : l.hasProposal ? 'PROPOSAL READY — APPLY RENDERS THE CUT' : 'TRANSCRIBED — AWAITING PROPOSAL';
  return (
    <>
      {[['LAB', l.lab], ['SEGMENTS', String(l.segments)], ['PROPOSAL', l.hasProposal ? '✓ READY' : '—'], ['OUTPUT', l.hasOutput ? '✓ RENDERED' : '—'], ['UPDATED', new Date(l.mtime).toLocaleString()]].map(([k, v]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0' }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{k}</span>
          <span style={{ ...mono(16, '.06em'), color: '#d7f2ff' }}>{v as string}</span>
        </div>
      ))}
      <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
        <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>PIPELINE</div>
        {['TRANSCRIBE', 'PROPOSE', 'APPLY', 'DEPLOY'].map((s, i) => {
          const done = i === 0 || (i === 1 && l.hasProposal) || (i === 2 && l.hasOutput);
          return (
            <div key={s} className="flex items-center" style={{ gap: 10, padding: '3px 0' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: done ? '#5df2b4' : 'rgba(125,232,255,.15)', boxShadow: done ? '0 0 8px #5df2b4' : 'none' }} />
              <span style={{ ...mono(16, '.2em'), color: done ? '#5df2b4' : '#56788c' }}>{s}</span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, ...display(23, 300), color: 'var(--uif-t-body)', lineHeight: 1.6 }}>{stage}</div>
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const funnelId = item.raw._funnelId || 'olga-erm';
  const [ret, setRet] = useState<any>(null);
  useEffect(() => {
    fetch(`/api/funnels/${funnelId}/retention?videoId=main-vsl&bucketSeconds=1`).then(r => r.json()).then(setRet).catch(() => { });
  }, [funnelId]);
  const buckets: any[] = ret?.retention || ret?.buckets || [];
  const path = buckets.length > 1
    ? buckets.map((b: any, i: number) => `${i === 0 ? 'M' : 'L'}${(i / (buckets.length - 1) * 320).toFixed(1)},${(88 - (b.pct ?? b.retention ?? b.viewers ?? 0) / Math.max(...buckets.map((x: any) => x.pct ?? x.retention ?? x.viewers ?? 1), 1) * 78).toFixed(1)}`).join(' ')
    : '';
  return (
    <>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>RETENTION · MAIN VSL · REAL VIEWERS</div>
      {path ? (
        <svg viewBox="0 0 320 90" style={{ width: '100%', height: 220 }} preserveAspectRatio="none">
          <path d={`${path} L320,90 L0,90 Z`} fill="rgba(158,240,255,.07)" stroke="none" />
          <path d={path} fill="none" stroke="#9ef0ff" strokeWidth="1.4" style={{ filter: 'drop-shadow(0 0 6px rgba(0,240,255,.5))' }} />
        </svg>
      ) : (
        <div style={{ ...display(23, 300), color: '#56788c' }}>Retention data unreachable for this funnel right now.</div>
      )}
      {buckets.length > 1 && (
        <div style={{ ...mono(13, '.14em'), color: '#49677a', marginTop: 8 }}>0:00 — {Math.floor(buckets.length / 60)}:{String(buckets.length % 60).padStart(2, '0')} · CLIFFS = STEEPEST DROPS</div>
      )}
    </>
  );
}

function Decide({ item, refresh, speak }: { item: OFItem; refresh: () => void; speak: (t: string) => void }) {
  const l = item.raw;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/funnels/videolab/apply?job=${l.lab}`).then(x => x.json());
      setMsg(r.error ? `✕ ${r.error}` : '✓ RENDER STARTED'); if (!r.error) speak('Render started — the proposed cut is being applied.'); refresh();
    } catch (e: any) { setMsg(`✕ ${e.message}`); } finally { setBusy(false); setTimeout(() => setMsg(''), 4000); }
  };
  return (
    <>
      {l.hasProposal && !l.hasOutput ? (
        <div onMouseUp={apply} style={{ ...bigVerb(), opacity: busy ? .6 : 1, textAlign: 'center' }}>{busy ? 'APPLYING…' : '✓ APPLY PROPOSAL'}</div>
      ) : l.hasOutput ? (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: '1px solid rgba(93,242,180,.5)', color: '#5df2b4', ...mono(18, '.26em') }}>OUTPUT READY · DEPLOY IN LAB</div>
      ) : (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: '1px solid rgba(255,195,107,.4)', color: '#ffc36b', ...mono(18, '.26em') }}>AWAITING PROPOSAL</div>
      )}
      {msg && <div style={{ ...mono(16, '.14em'), color: msg.startsWith('✓') ? '#5df2b4' : '#ff7d8a', marginTop: 10, textAlign: 'center' }}>{msg}</div>}
      <a href="/funnels/video-lab" style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>OPEN VIDEO LAB ↗</div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>PROPOSE → APPLY → DEPLOY · REAL PIPELINE</div>
    </>
  );
}

export function VideoDeckSurface({ funnelId, slug }: { funnelId?: string | null; slug?: string }) {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/funnels/videolab/transcribe').then(r => r.json()).then(d => {
      /* scope to the project when opened from a project deck — labs are named <slug>-* */
      const labs: any[] = (d.labs || []).filter((l: any) => !slug || l.lab.startsWith(slug) || l.lab.startsWith(funnelId || '\u0000'));
      setItems(labs.map((l: any): OFItem => ({
        id: l.lab, title: l.lab.toUpperCase().replace(/-/g, ' '), type: 'VSL LAB',
        status: l.hasOutput ? 'fixed' : l.hasProposal ? 'review' : 'draft',
        kpis: [
          { l: 'SEGMENTS', v: String(l.segments) },
          { l: 'PROPOSAL', v: l.hasProposal ? '✓' : '—' },
          { l: 'OUTPUT', v: l.hasOutput ? '✓' : '—' },
        ],
        foot: `updated ${new Date(l.mtime).toLocaleDateString()}`,
        raw: { ...l, _funnelId: funnelId || (l.lab.startsWith('olga') ? 'olga-erm' : undefined) },
      })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [funnelId, slug]);
  useEffect(() => { load(); }, [load]);

  const adapter: OFAdapter = {
    kind: 'video',
    strap: (its) => `ONE FOCUS · ${its.length} VSL LABS · PROPOSE → APPLY → DEPLOY`,
    decidable: (it) => it.raw.hasProposal && !it.raw.hasOutput,
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
